<template>
  <div class="relative flex">
    <!-- Menu trigger: 'dots' (overflow ellipses, default — a real VButton
         secondary, so the trigger reads as the sibling actions' twin and
         can never drift from the button recipe) or 'pill' (select-style:
         menu_button slot + chevron, e.g. stage quick-move). -->
    <VButton
      v-if="variant !== 'pill'"
      :ref="setMenuButton"
      :click="toggleMenu"
      variant="secondary"
      :size="triggerSize"
      class="!inline-flex w-fit items-center justify-center"
      aria-haspopup="menu"
      :aria-expanded="open"
    >
      <slot v-if="$slots.menu_button" name="menu_button"></slot>
      <template v-else>
        <DotsHorizontal class="h-4 w-4 text-gray-700" />
        <span
          v-if="indicator"
          class="absolute right-0 top-0 block h-1.5 w-1.5 rounded-full bg-[rgb(126,173,219)] ring-2 ring-white"
        />
      </template>
    </VButton>
    <button
      v-else
      ref="menuButton"
      type="button"
      class="elevation-low inline-flex h-6 w-fit items-center justify-center gap-1.5 rounded-full bg-white pl-2.5 pr-1.5 text-xs font-medium text-gray-700 focus:outline-none active:translate-y-px"
      aria-haspopup="menu"
      :aria-expanded="open"
      @click="toggleMenu"
    >
      <slot name="menu_button"></slot>
      <ChevronDown :size="12" class="flex-shrink-0 text-gray-400" />
    </button>

    <!-- Dropdown Menu -->
    <Teleport :to="menuTarget">
      <div
        v-if="hasOpened"
        ref="menuItems"
        role="menu"
        class="actions-menu-scrollbar elevation-high absolute z-10 w-64 divide-y-4 divide-gray-100 rounded-md bg-white py-1 transition duration-100 focus:outline-none"
        :style="menuStyles"
        :class="{
          'pointer-events-none scale-95 opacity-0': !open,
          'pointer-events-auto scale-100 opacity-100': open,
        }"
      >
        <slot></slot>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, computed, watch, useSlots } from 'vue'
import DotsHorizontal from './icons/dots-horizontal.vue'
import { ChevronDown } from 'lucide-vue-next'
import { onClickOutside, useMagicKeys } from '@vueuse/core'
import VButton from './v-button.vue'

const props = defineProps({
  indicator: {
    type: Boolean,
    default: false,
  },
  // 'dots' — overflow ellipses (default). 'pill' — select-style trigger that
  // renders the menu_button slot plus a chevron (e.g. the stage quick-move).
  variant: {
    type: String,
    default: 'dots',
  },
  // The dots trigger is a VButton secondary — this is its size, so card
  // headers can pair it with their sibling pencil ('xs') and denser rows
  // can go smaller/larger without touching the recipe.
  triggerSize: {
    type: String,
    default: 'xs',
  },
})

const open = ref(false)
// Menus mount hidden behind a Teleport — pipeline-scale pages render
// thousands of them. The panel (and its slot content) only mounts after
// the first open and stays mounted afterwards for close/reopen.
const hasOpened = ref(false)
const menuButton = ref<HTMLElement | null>(null)
const menuItems = ref<HTMLElement | null>(null)

// The dots trigger is a component (VButton) — resolve its root element for
// positioning/focus; the pill branch's native button passes the element
// straight through.
const setMenuButton = (el: unknown) => {
  menuButton.value = el && typeof el === 'object' && '$el' in el ? (el.$el as HTMLElement) : (el as HTMLElement | null)
}

// Teleport into the nearest portal container, not <body>. Page-level menus
// go to the app shell (.v-recede-surface); the shell is position: fixed,
// which is a stacking context: body-level teleports float above ALL app
// chrome regardless of z-index, so a menu scrolled under the sticky
// pipeline toolbar painted over it. Inside the shell the menu's z-10
// competes with (and loses to) the toolbar's z-30, while body-level modals
// and toasts still cover menus. Menus triggered inside a modal (VModal
// tags its fixed container with data-menu-portal) teleport there instead:
// the Dialog wrapper is a relative z-50 stacking context, so a shell-level
// z-10 panel would paint behind the modal surface. Resolved lazily on
// first open (the panel mounts then anyway); body is the fallback for
// mounts outside the app shell (Storybook, tests).
const menuTarget = ref<string | HTMLElement>('body')
const resolveMenuTarget = () => {
  if (menuTarget.value === 'body') {
    menuTarget.value =
      menuButton.value?.closest<HTMLElement>('[data-menu-portal]') ??
      document.querySelector<HTMLElement>('.v-recede-surface') ??
      document.body
  }
}

// VButton's click prop carries no event — the trigger element is already
// held by the menuButton ref, so the toggle needs nothing from the caller.
const toggleMenu = () => {
  open.value = !open.value
  if (open.value) {
    hasOpened.value = true
    resolveMenuTarget()
    // Re-decide the direction every open; scroll/resize repositionings keep
    // it so the panel never jumps sides mid-scroll.
    directionDecided.value = false
    autoPosition()
  }
}

const closeMenu = () => {
  if (!open.value) return
  open.value = false
  // Escape closes and hands focus back to the trigger.
  menuButton.value?.focus()
}

const openMenu = () => {
  if (open.value) return
  open.value = true
  hasOpened.value = true
  resolveMenuTarget()
  directionDecided.value = false
  autoPosition()
}

defineExpose({ open: openMenu, close: closeMenu })

// Seat the menu a few px clear of the trigger (Tailwind UI dropdowns use
// a visible mt-2 gap) — flush or overlapping edges fight the button's
// elevation shadow and read as a seam or as covering the button.
const TRIGGER_GAP = 4

// Direction is decided once per open and kept for the menu's lifetime.
// Below is the default; above only when the menu cannot fit under the
// trigger (e.g. the builder's bottom add-question divider, the last
// element of the document — no scrollable space left to reveal a
// below-fold menu) and there is room above. Re-flipping on every scroll
// event made the panel jump between positions, which is why the side is
// sticky. Below keeps the height clamp; above fits by construction.
const openAbove = ref(false)
const directionDecided = ref(false)

const autoPosition = () => {
  // Every mounted ActionsMenu listens to scroll/resize (capture phase), so
  // list-scale pages fire this thousands of times per scroll tick — a
  // closed menu must cost nothing here. The refs check waits a tick: on
  // first open the panel is still mounting (v-if="hasOpened").
  if (!open.value) return
  nextTick(() => {
    if (!menuButton.value || !menuItems.value) return

    const buttonRect = menuButton.value.getBoundingClientRect()

    // The teleported parent (a data-menu-portal modal container, the app
    // shell, or body) is the containing block and is viewport-boxed (fixed
    // inset-0 / the document box), so button client-rect coordinates apply —
    // offset by the container's own rect for containers that aren't anchored
    // at the viewport origin (e.g. VModal's `fixed inset-0 top-1` sits 4px
    // down).
    const menu = menuItems.value!
    // Resolved portals are always elements (body is the fallback).
    const containerRect = (menuTarget.value as HTMLElement).getBoundingClientRect()

    if (!directionDecided.value) {
      // Measure the natural height before any clamp — a stale maxHeight
      // from a previous below-fold open would shrink the measurement.
      menu.style.maxHeight = ''
      menu.style.overflowY = ''
      const spaceBelow = window.innerHeight - buttonRect.bottom - TRIGGER_GAP
      openAbove.value = menu.offsetHeight > spaceBelow && menu.offsetHeight <= buttonRect.top - TRIGGER_GAP
      directionDecided.value = true
    }

    if (openAbove.value) {
      // Flipped above: fits by construction, so height stays unclamped.
      menu.style.top = `${buttonRect.top - TRIGGER_GAP - menu.offsetHeight - containerRect.top}px`
      menu.style.right = `${containerRect.right - buttonRect.right}px`
      return
    }

    // Below: clamp the panel to the space left under the trigger so tall
    // menus scroll internally (.actions-menu-scrollbar) instead of running
    // past the fold. Same formula on every evaluation — nothing flips or
    // jumps mid-scroll.
    menu.style.top = `${buttonRect.bottom + TRIGGER_GAP - containerRect.top}px`
    menu.style.right = `${containerRect.right - buttonRect.right}px`
    const viewportPad = 8
    const available = window.innerHeight - buttonRect.bottom - TRIGGER_GAP - viewportPad
    menu.style.maxHeight = `${Math.max(available, 0)}px`
    menu.style.overflowY = 'auto'
  })
}

onClickOutside(menuButton, (e: MouseEvent) => {
  if (!open.value) return

  const target = e.target as Node
  if (menuButton.value?.contains(target) || menuItems.value?.contains(target)) {
    return
  }

  open.value = false
})

// --- Keyboard bindings (active while the menu is open) ---
const { escape, arrowDown, arrowUp, home, end } = useMagicKeys()

const menuButtons = (): HTMLElement[] =>
  menuItems.value ? Array.from(menuItems.value.querySelectorAll<HTMLElement>('button:not(:disabled)')) : []

const focusIndex = (index: number) => {
  const items = menuButtons()
  if (items.length) items[(index + items.length) % items.length].focus()
}

const rotateFocus = (direction: 1 | -1) => {
  if (!open.value) return
  const items = menuButtons()
  const current = items.indexOf(document.activeElement as HTMLElement)
  focusIndex(current === -1 ? (direction === 1 ? 0 : -1) : current + direction)
}

watch(escape, (pressed) => pressed && open.value && closeMenu())
watch(arrowDown, (pressed) => pressed && rotateFocus(1))
watch(arrowUp, (pressed) => pressed && rotateFocus(-1))
watch(home, (pressed) => pressed && open.value && focusIndex(0))
watch(end, (pressed) => pressed && open.value && focusIndex(-1))

// Reactive so the fold-out animation's origin follows the chosen direction
// (the panel animates out of the edge nearest its trigger).
const menuStyles = computed(() => ({
  position: 'absolute' as const,
  transformOrigin: openAbove.value ? 'bottom right' : 'top right',
}))

const handleScroll = (e: Event) => {
  if (menuItems.value && e.target instanceof Node && menuItems.value.contains(e.target)) return
  autoPosition()
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll, true)
  window.addEventListener('resize', autoPosition)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll, true)
  window.removeEventListener('resize', autoPosition)
})
</script>

<style>
/* Unscoped because the menu is teleported to <body>; namespaced via .actions-menu-scrollbar so the rules don't leak. */
.actions-menu-scrollbar {
  scrollbar-color: #d1d5db transparent; /* Firefox: thumb / track */
}
.actions-menu-scrollbar::-webkit-scrollbar {
  width: 10px;
}
.actions-menu-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.actions-menu-scrollbar::-webkit-scrollbar-thumb {
  background-color: #d1d5db;
  border-radius: 8px;
}
.actions-menu-scrollbar::-webkit-scrollbar-thumb:hover {
  background-color: #9ca3af;
}
</style>
